import { useCallback, useState } from 'react';
import { useApi, API_BASE_URL } from './useApi';

interface OnboardingStatus {
  onboardingCompleted: boolean;
  usageType?: string;
  approved: boolean;
}

interface OnboardingData {
  usageType: string;
  firmName?: string;
  firmSize?: string;
  role?: string;
  stateOfRecord?: string;
  barNumber?: string;
  referralSource?: string;
  // Legal acceptance evidence carried from the registration handoff (SOR-37);
  // names/types must stay in sync with OnboardingSchema on the api-gateway.
  termsVersionAccepted?: number;
  privacyVersionAccepted?: number;
  [key: string]: unknown;
}

interface OnboardingCompleteResponse {
  success: boolean;
  message: string;
  organizationId?: string;
}

interface UseOnboardingApiReturn {
  getOnboardingStatus: () => Promise<OnboardingStatus>;
  completeOnboarding: (data: OnboardingData) => Promise<OnboardingCompleteResponse>;
  loading: boolean;
  error: string | null;
}

/**
 * Custom hook for onboarding API calls
 */
export function useOnboardingApi(): UseOnboardingApiReturn {
  const { get, post } = useApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Check if user has completed onboarding
   */
  const getOnboardingStatus = useCallback(async (): Promise<OnboardingStatus> => {
    setLoading(true);
    setError(null);
    try {
      const result = await get<OnboardingStatus>(`${API_BASE_URL}/api/onboarding/status`);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [get]);

  /**
   * Complete onboarding for the current user
   */
  const completeOnboarding = useCallback(
    async (data: OnboardingData): Promise<OnboardingCompleteResponse> => {
      setLoading(true);
      setError(null);
      try {
        const result = await post<OnboardingCompleteResponse>(`${API_BASE_URL}/api/onboarding/complete`, data);
        return result;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [post]
  );

  return {
    getOnboardingStatus,
    completeOnboarding,
    loading,
    error,
  };
}
